import { SELECTORS, scaleValue } from '@/scripts/core/global'
import { TsDOM as q } from '@/scripts/core/TsDOM'
import { type StickyHeader } from '@/scripts/theme/sticky-header'

const MENU_CLOSE_ANIMATION_MS = 340
const MENU_HOVER_CLOSE_DELAY_MS = 150

const MENU_SELECTORS = {
	menuContent:
		'[id^="MegaMenu-Content"], [id^="HeaderMenu-MenuList"], [id^="HeaderMenu-SubMenuList"]',
	caretButton: '.header__menu-caret',
	menuWrapper: '.header__menu-item-wrapper',
	submenuWrapper: '.header__submenu-item-wrapper',
} as const

export class HeaderMenu extends HTMLElement {
	static htmlSelector = 'header-menu'

	header?: StickyHeader
	hoverCloseTimeout?: number
	closeAnimationTimeout?: number
	menuContent?: HTMLElement
	caretButton?: HTMLButtonElement
	detailsId = ''
	isReady = false

	constructor() {
		super()
		this.handleCaretClick = this.handleCaretClick.bind(this)
		this.handleCaretKeydown = this.handleCaretKeydown.bind(this)
		this.handleMenuContentKeydown = this.handleMenuContentKeydown.bind(this)
		this.handleFocusOut = this.handleFocusOut.bind(this)
		this.handleDocumentClick = this.handleDocumentClick.bind(this)
		this.handleWrapperMouseEnter = this.handleWrapperMouseEnter.bind(this)
		this.handleMenuContentMouseEnter = this.handleMenuContentMouseEnter.bind(this)
		this.handleMouseLeave = this.handleMouseLeave.bind(this)
	}

	connectedCallback() {
		if (this.isReady) return
		this.header = q.os<StickyHeader>(SELECTORS.headerWrapper)
		this.menuContent = q.os(MENU_SELECTORS.menuContent, this)
		this.caretButton = q.os<HTMLButtonElement>(MENU_SELECTORS.caretButton, this)
		this.detailsId =
			this.id ||
			q.os<HTMLElement>(
				`${MENU_SELECTORS.menuWrapper}, ${MENU_SELECTORS.submenuWrapper}`,
				this
			)?.id ||
			''
		this.init()
		this.isReady = true
	}

	disconnectedCallback() {
		this.cleanup()
		this.isReady = false
	}

	init() {
		if (!this.caretButton) return

		this.caretButton.addEventListener('click', this.handleCaretClick)
		this.caretButton.addEventListener('keydown', this.handleCaretKeydown)
		this.menuContent?.addEventListener('keydown', this.handleMenuContentKeydown)
		this.addEventListener('focusout', this.handleFocusOut)
		document.addEventListener('click', this.handleDocumentClick)

		if (!this.caretButton.hasAttribute('data-hover-open')) return
		const wrapper = q.os<HTMLElement>(
			`${MENU_SELECTORS.menuWrapper}, ${MENU_SELECTORS.submenuWrapper}`,
			this
		)
		wrapper?.addEventListener('mouseenter', this.handleWrapperMouseEnter)
		this.menuContent?.addEventListener('mouseenter', this.handleMenuContentMouseEnter)
		this.addEventListener('mouseleave', this.handleMouseLeave)
	}

	cleanup() {
		this.caretButton?.removeEventListener('click', this.handleCaretClick)
		this.caretButton?.removeEventListener('keydown', this.handleCaretKeydown)
		this.menuContent?.removeEventListener('keydown', this.handleMenuContentKeydown)
		this.menuContent?.removeEventListener('mouseenter', this.handleMenuContentMouseEnter)

		const wrapper = q.os<HTMLElement>(
			`${MENU_SELECTORS.menuWrapper}, ${MENU_SELECTORS.submenuWrapper}`,
			this
		)
		wrapper?.removeEventListener('mouseenter', this.handleWrapperMouseEnter)

		this.removeEventListener('mouseleave', this.handleMouseLeave)
		this.removeEventListener('focusout', this.handleFocusOut)
		document.removeEventListener('click', this.handleDocumentClick)

		if (this.hoverCloseTimeout) window.clearTimeout(this.hoverCloseTimeout)
		if (this.closeAnimationTimeout) window.clearTimeout(this.closeAnimationTimeout)
	}

	handleCaretClick(event: MouseEvent) {
		event.preventDefault()
		event.stopPropagation()
		this.hasAttribute('open') ? this.close() : this.open()
	}

	handleCaretKeydown(event: KeyboardEvent) {
		if (['Enter', ' ', 'ArrowDown'].includes(event.key)) {
			event.preventDefault()
			this.open()
			requestAnimationFrame(() => {
				const firstFocusable = this.menuContent?.querySelector<HTMLElement>(
					'a[href], button:not([disabled])'
				)
				firstFocusable?.focus()
			})
			return
		}

		if (event.key === 'ArrowUp' && this.hasAttribute('open')) {
			event.preventDefault()
			const links = this.menuContent?.querySelectorAll<HTMLElement>('a[href]')
			if (!links || links.length === 0) return
			links[links.length - 1].focus()
			return
		}

		if (event.key === 'Escape' && this.hasAttribute('open')) {
			event.preventDefault()
			this.close()
			this.caretButton?.focus()
		}
	}

	handleMenuContentKeydown(event: KeyboardEvent) {
		if (event.key !== 'Escape' || !this.hasAttribute('open')) return
		event.preventDefault()
		this.close()
		this.caretButton?.focus()
	}

	handleFocusOut() {
		window.setTimeout(() => {
			if (!this.contains(document.activeElement)) this.close()
		}, 0)
	}

	handleDocumentClick(event: MouseEvent) {
		const target = q.rt(event)
		if (this.hasAttribute('open') && !this.contains(target)) this.close()
	}

	handleWrapperMouseEnter() {
		if (this.hoverCloseTimeout) window.clearTimeout(this.hoverCloseTimeout)
		this.open()
	}

	handleMenuContentMouseEnter() {
		if (this.hoverCloseTimeout) window.clearTimeout(this.hoverCloseTimeout)
	}

	handleMouseLeave(event: MouseEvent) {
		const relatedTarget = event.relatedTarget
		if (relatedTarget instanceof Node && this.contains(relatedTarget)) return
		this.hoverCloseTimeout = window.setTimeout(
			() => this.close(),
			MENU_HOVER_CLOSE_DELAY_MS
		)
	}

	updateHeaderBottomPosition() {
		if (!this.header) return
		if (
			document.documentElement.style.getPropertyValue(
				'--header-bottom-position-desktop'
			) !== ''
		)
			return
		const viewport = window.innerWidth >= 750 ? 'desktop' : 'mobile'
		document.documentElement.style.setProperty(
			'--header-bottom-position-desktop',
			`calc(${scaleValue(this.header.getBoundingClientRect().bottom, viewport)} * var(--ax))`
		)
	}

	open() {
		if (this.hoverCloseTimeout) window.clearTimeout(this.hoverCloseTimeout)

		if (this.hasAttribute('open') && !this.hasAttribute('data-uc-closing')) {
			this.caretButton?.setAttribute('aria-expanded', 'true')
			return
		}

		if (this.closeAnimationTimeout) {
			window.clearTimeout(this.closeAnimationTimeout)
			this.closeAnimationTimeout = undefined
		}

		this.removeAttribute('data-uc-closing')

		document.querySelectorAll<HeaderMenu>(HeaderMenu.htmlSelector).forEach((menu) => {
			if (menu === this) return
			menu.close()
		})

		if (window.Ucoast) window.Ucoast.openMenuId = this.detailsId
		this.header && (this.header.preventHide = true)
		this.caretButton?.setAttribute('aria-expanded', 'true')
		this.updateHeaderBottomPosition()

		const isMegaMenu = this.classList.contains('mega-menu')
		if (!this.menuContent) {
			this.setAttribute('open', '')
			return
		}

		const wasHidden = this.menuContent.hidden
		this.menuContent.hidden = false

		if (isMegaMenu && wasHidden) {
			this.removeAttribute('open')
			requestAnimationFrame(() => {
				requestAnimationFrame(() => {
					this.setAttribute('open', '')
				})
			})
		} else {
			this.setAttribute('open', '')
		}

		window.Ucoast?.mediaManager?.playAllInContainer(this)
	}

	close() {
		if (!this.hasAttribute('open') && !this.hasAttribute('data-uc-closing')) return

		this.removeAttribute('open')
		this.caretButton?.setAttribute('aria-expanded', 'false')

		const isMegaMenu = this.classList.contains('mega-menu')

		if (this.closeAnimationTimeout) {
			window.clearTimeout(this.closeAnimationTimeout)
			this.closeAnimationTimeout = undefined
		}

		if (this.menuContent) {
			if (isMegaMenu) {
				this.setAttribute('data-uc-closing', '')
				const menuContent = this.menuContent
				this.closeAnimationTimeout = window.setTimeout(() => {
					if (!this.hasAttribute('open')) {
						menuContent.hidden = true
						this.removeAttribute('data-uc-closing')
					}
					this.closeAnimationTimeout = undefined
				}, MENU_CLOSE_ANIMATION_MS)
			} else {
				this.menuContent.hidden = true
				this.removeAttribute('data-uc-closing')
			}
		}

		if (window.Ucoast?.openMenuId === this.detailsId) {
			window.Ucoast.openMenuId = undefined
		}
		if (this.header) this.header.preventHide = false
	}
}
